import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState, useContext, useEffect, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { authService } from "../../services/apiService";
import { AuthContext } from "../../contexts/AuthContext";

const LoginForm = ({ hasLabel = false, layout = "simple" }) => {
  const { login, logout } = useContext(AuthContext);
  const { setAutenticado } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // ✅ Memoizar query para evitar recrearlo en cada render (causaba loop por dependencias)
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  /*const query = useQuery();
  const _token = query.get('token')
  const _modulo = query.get('modulo')

  console.log("Token: ", _token)
  console.log("Modulo: ", _modulo)*/

  // State
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
    id_empresa: "",
  });

  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const hasRedirected = useRef(false);


  //  const getRedirectPath = () => {
  //   // 1. Prioridad a parámetro 'redirect' en URL
  //   const redirectParam = query.get('redirect');
  //   if (redirectParam) {
  //     console.log('🔗 Redirigiendo por parámetro URL:', redirectParam);
  //     return `/${redirectParam}`;
  //   }

  //   // 2. Prioridad a parámetro 'modulo' en URL (compatibilidad)
  //   const moduloParam = query.get('modulo');
  //   if (moduloParam) {
  //     console.log('🔗 Redirigiendo por módulo:', moduloParam);
  //     return `/${moduloParam}`;
  //   }

  //   // 3. Revisar si hay vista específica
  //   const vistaParam = query.get('vista');
  //   if (moduloParam && vistaParam) {
  //     console.log('🔗 Redirigiendo con vista:', `${moduloParam}?vista=${vistaParam}`);
  //     return `/${moduloParam}?vista=${vistaParam}`;
  //   }

  //   // 4. Revisar location.state para redirectTo
  //   const stateRedirect = location.state?.redirectTo;
  //   if (stateRedirect) {
  //     console.log('📮 Redirigiendo por state:', stateRedirect);
  //     return stateRedirect;
  //   }

  //   // 5. Default: página principal
  //   console.log('🏠 Redirigiendo a página principal');
  //   return "/";
  // };

  const getRedirectPath = useCallback(() => {
    // 1. Prioridad a parámetro 'redirect' en URL
    const redirectParam = query.get('redirect');
    if (redirectParam) {
      console.log('🔗 Redirigiendo por parámetro URL:', redirectParam);

      // ✅ CONSTRUIR URL CON PARÁMETROS ADICIONALES
      let redirectUrl = `/${redirectParam}`;
      const urlParams = new URLSearchParams();

      // ✅ AGREGAR PARÁMETROS ESPECÍFICOS DEL COMPONENTE
      if (redirectParam === 'reporte_alertas') {
        const reportType = query.get('report_type') || query.get('tipo_reporte');
        if (reportType) {
          urlParams.append('report_type', reportType);
        }

        // Otros parámetros específicos para alertas
        const vista = query.get('vista');
        if (vista) {
          urlParams.append('vista', vista);
        }
      }

      // ✅ PARÁMETROS PARA OTROS REPORTES (FUTURA EXPANSIÓN)
      if (redirectParam === 'reporte_recorridos') {
        const tipoVista = query.get('tipo_vista');
        if (tipoVista) {
          urlParams.append('tipo_vista', tipoVista);
        }
      }

      if (redirectParam === 'reporte_usabilidad') {
        const periodo = query.get('periodo');
        if (periodo) {
          urlParams.append('periodo', periodo);
        }
      }

      // ✅ CONSTRUIR URL FINAL
      if (urlParams.toString()) {
        redirectUrl += `?${urlParams.toString()}`;
      }

      console.log('🔗 URL de redirección construida:', redirectUrl);
      return redirectUrl;
    }

    // 2. Prioridad a parámetro 'modulo' en URL (compatibilidad)
    const moduloParam = query.get('modulo');
    if (moduloParam) {
      console.log('🔗 Redirigiendo por módulo:', moduloParam);

      // ✅ MANTENER PARÁMETROS ADICIONALES CON MÓDULO
      const vista = query.get('vista');
      if (vista) {
        return `/${moduloParam}?vista=${vista}`;
      }

      return `/${moduloParam}`;
    }

    // 3. Revisar location.state para redirectTo
    const stateRedirect = location.state?.redirectTo;
    if (stateRedirect) {
      console.log('📮 Redirigiendo por state:', stateRedirect);
      return stateRedirect;
    }

    // 4. Default: página principal
    console.log('🏠 Redirigiendo a página principal');
    return "/";
  }, [query, location.state]);

  const performAutoLogin = useCallback(async (loginData) => {
    try {
      console.log('🔄 Iniciando login automático con datos:', loginData);

      const postData = {
        id_empresa: loginData.id_empresa,
        usuario: loginData.usuario,
        contrasenia: loginData.contrasenia,
      };

      const response = await authService.login(postData);

      if (response && response.statusCode === 200) {
        toast.success("Inicio de sesión automático exitoso", { theme: "colored" });

        // Almacenar token y redirigir
        const { token } = response.data;
        let tokenConsumo = {
          userId: postData.id_empresa,
          username: postData.usuario,
          password: postData.contrasenia
        };

        login(token, tokenConsumo, loginData.id_empresa);
        setAutenticado(true);
        //navigate("/");
        const redirectPath = getRedirectPath();
        navigate(redirectPath, { replace: true });
      } else {
        console.error('❌ Error en login automático:', response);
        toast.error("Error en el login automático. Usa el formulario manual.", {
          theme: "colored",
        });
        setIsAutoLogin(false); // Mostrar formulario manual
      }
    } catch (error) {
      console.error('❌ Error en login automático:', error);
      toast.error("Error en el login automático. Usa el formulario manual.", {
        theme: "colored",
      });
      setIsAutoLogin(false); // Mostrar formulario manual
    }
  }, [getRedirectPath, login, navigate, setAutenticado]);

  // ✅ DETECTAR DATOS DE LOGIN EN URL O POST AL MONTAR EL COMPONENTE
  useEffect(() => {
    const checkAutoLogin = async () => {
      // Verificar si ya está autenticado
      const auth = localStorage.getItem("auth");
      // Si hay datos de login por URL, forzar logout antes de continuar
      const id_empresa_url = query.get('id_empresa');
      const usuario_url = query.get('usuario') || query.get('email');
      let contrasenia_url = query.get('contrasenia') || query.get('password');

      //const contrasenia_url = query.get('contrasenia') || query.get('password');

      function decodePassword(encoded) {
        if (!encoded) return "";
        try {
          // 1. Decodificar URL
          const urlDecoded = decodeURIComponent(encoded);
          // 2. Decodificar Base64
          const decoded = atob(urlDecoded);
          return decoded;
        } catch (e) {
          console.error("Error decodificando contraseña:", e);
          return "";
        }
      }

      //contrasenia_url ='';
      contrasenia_url = decodePassword(contrasenia_url);

      if (id_empresa_url && usuario_url && contrasenia_url) {
        if (auth === "true") {
          // Cerrar sesión previa antes de login automático por URL
          logout();
        }
        console.log('🔗 Datos de login detectados en URL');
        setIsAutoLogin(true);
        await performAutoLogin({
          id_empresa: id_empresa_url,
          usuario: usuario_url,
          contrasenia: contrasenia_url
        });
        return;
      }

      // ✅ OPCIÓN 2: DATOS POR POST (desde location.state)
      const postData = location.state?.loginData;
      if (postData?.id_empresa && postData?.usuario && postData?.contrasenia) {
        console.log('📮 Datos de login detectados por POST');
        setIsAutoLogin(true);
        await performAutoLogin(postData);
        return;
      }

      // ✅ OPCIÓN 3: DATOS EN LOCALSTORAGE (para persistencia temporal)
      const autoLoginData = localStorage.getItem('autoLoginData');
      if (autoLoginData) {
        try {
          const parsedData = JSON.parse(autoLoginData);
          if (parsedData.id_empresa && parsedData.usuario && parsedData.contrasenia) {
            console.log('💾 Datos de login detectados en localStorage');
            localStorage.removeItem('autoLoginData'); // Limpiar después de usar
            setIsAutoLogin(true);
            await performAutoLogin(parsedData);
            return;
          }
        } catch (error) {
          console.error('Error parsing autoLoginData:', error);
          localStorage.removeItem('autoLoginData');
        }
      }

      // ✅ SI YA ESTÁ AUTENTICADO Y NO HUBO AUTOLOGIN, REDIRIGIR UNA SOLA VEZ
      const token = localStorage.getItem("token");
      if (auth === "true" && token) {
        if (hasRedirected.current) return;
        hasRedirected.current = true;

        setAutenticado(true);
        const redirectPath = getRedirectPath();
        console.log('🔀 [useEffect] Ya autenticado, redirigiendo a:', redirectPath);
        navigate(redirectPath, { replace: true });
        return;
      }

      // ✅ LIMPIAR ESTADO INCONSISTENTE: auth sin token
      if (auth === "true" && !token) {
        console.warn('⚠️ Estado inconsistente: auth=true pero sin token. Limpiando...');
        localStorage.removeItem("auth");
        logout();
      }

      // Si no hay datos automáticos, mostrar formulario normal
      console.log('📝 No se detectaron datos automáticos, mostrando formulario');
      setIsAutoLogin(false);
    };

    checkAutoLogin();
  }, [setAutenticado, navigate, location.pathname, location.search, location.state, query, logout, getRedirectPath, performAutoLogin]);


  // useEffect(() => {

  //   const auth = localStorage.getItem("auth");
  //   if (auth === "true") {
  //     setAutenticado(true);
  //     navigate("/");
  //   }
  // }, [setAutenticado, navigate]);

  // // Handler
  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   try {
  //     const postData = {
  //       id_empresa: formData.id_empresa,
  //       usuario: formData.email,
  //       contrasenia: formData.password,
  //     };
  //     const response = await authService.login(postData);
  //     if (response && response.statusCode === 200) {
  //       toast.success("Inicio de sesión exitoso", { theme: "colored" });

  //       // Almacena el token y redirige al inicio
  //       const { token } = response.data;
  //       let tokenConsumo = { userId: postData.id_empresa, username: postData.usuario, password: postData.contrasenia };
  //       login(token, tokenConsumo);
  //       setAutenticado(true);
  //       navigate("/");
  //     } else {
  //       toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
  //         theme: "colored",
  //       });
  //     }
  //   } catch (error) {
  //     toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
  //       theme: "colored",
  //     });
  //     console.error("Error en la solicitud:", error); // Imprime el error en la consola
  //   }
  // };

  // const handleFieldChange = (e) => {
  //   setFormData({
  //     ...formData,
  //     [e.target.name]: e.target.value,
  //   });
  // };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   try {
  //     const postData = {
  //       id_empresa: formData.id_empresa,
  //       usuario: formData.email,
  //       contrasenia: formData.password,
  //     };

  //     const response = await authService.login(postData);

  //     if (response && response.statusCode === 200) {
  //       toast.success("Inicio de sesión exitoso", { theme: "colored" });

  //       // Almacena el token y redirige al inicio
  //       const { token } = response.data;
  //       let tokenConsumo = { 
  //         userId: postData.id_empresa, 
  //         username: postData.usuario, 
  //         password: postData.contrasenia 
  //       };

  //       login(token, tokenConsumo);
  //       setAutenticado(true);
  //       navigate("/");
  //     } else {
  //       toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
  //         theme: "colored",
  //       });
  //     }
  //   } catch (error) {
  //     toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
  //       theme: "colored",
  //     });
  //     console.error("Error en la solicitud:", error);
  //   }
  // };

  // ✅ Eliminado useEffect duplicado de redirección: la lógica vive en el efecto principal.

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const postData = {
        id_empresa: formData.id_empresa,
        usuario: formData.email,
        contrasenia: formData.password,
      };

      const response = await authService.login(postData);

      if (response && response.statusCode === 200) {
        toast.success("Inicio de sesión exitoso", { theme: "colored" });

        const { token } = response.data;
        let tokenConsumo = {
          userId: postData.id_empresa,
          username: postData.usuario,
          password: postData.contrasenia
        };

        login(token, tokenConsumo, formData.id_empresa);
        setAutenticado(true);

        // ✅ USAR REDIRECCIÓN DINÁMICA CON LOG
        const redirectPath = getRedirectPath();
        console.log('🔀 [handleSubmit] Redirigiendo a:', redirectPath);
        navigate(redirectPath);

      } else {
        toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
          theme: "colored",
        });
      }
    } catch (error) {
      toast.error("Error al iniciar sesión. Verifica tus credenciales.", {
        theme: "colored",
      });
      console.error("Error en la solicitud:", error);
    }
  };



  const handleFieldChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ✅ MOSTRAR LOADING SI ES AUTO LOGIN
  if (isAutoLogin) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Iniciando sesión...</span>
        </div>
        <p className="mt-3">Iniciando sesión automáticamente...</p>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setIsAutoLogin(false)}
        >
          Usar formulario manual
        </Button>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="align-items-center justify-content-between">
        <Col xs={12} md={4} className="pe-1">
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>Empresa</Form.Label>}
            <Form.Control
              placeholder={!hasLabel ? "Id" : ""}
              value={formData.id_empresa}
              name="id_empresa"
              onChange={handleFieldChange}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={8} className="pe-1">
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>Usuario</Form.Label>}
            <Form.Control
              placeholder={!hasLabel ? "Usuario" : ""}
              value={formData.email}
              name="email"
              onChange={handleFieldChange}
              type="text"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="align-items-center justify-content-between">
        <Col xs={12} md={12} className="pe-1">
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>Contraseña</Form.Label>}
            <Form.Control
              placeholder={!hasLabel ? "Contraseña" : ""}
              value={formData.password}
              name="password"
              onChange={handleFieldChange}
              type="password"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="justify-content-between align-items-center">
        <Col xs="auto">
          <Form.Check type="checkbox" id="rememberMe" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember"
              checked={formData.remember}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  remember: e.target.checked,
                })
              }
            />
            <Form.Check.Label className="mb-0 text-700">
              Remember me
            </Form.Check.Label>
          </Form.Check>
        </Col>
      </Row>

      <Row className="align-items-center justify-content-between">
        <Col xs={12} md={12} className="pe-1">
          <Form.Group>
            <Button
              type="submit"
              variant="default"
              className="mt-3 py-3 w-100 rounded-4 btn-primary-alt"
              disabled={!formData.email || !formData.password}
            >
              Iniciar sesión
            </Button>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

LoginForm.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool,
};

export default LoginForm;
